import { useState, useEffect } from 'react'
import { sanityFetch } from '../sanityClient'

// Generic hook — fetches any GROQ query and returns { data, loading, error }.
// Re-fetches when query or params change.
export function useSanityData(query, params = {}) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  useEffect(() => {
    if (!query) return
    let cancelled = false
    setLoading(true)
    setError(null)
    sanityFetch(query, params)
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err)
          setLoading(false)
        }
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, JSON.stringify(params)])

  return { data, loading, error }
}

// Convenience hook — fetch a single page document by slug
export function usePage(slug) {
  const query = slug ? `*[_type == "page" && slug.current == $slug][0]{
    _id, title, slug, navSection, navOrder,
    modules[] {
      ...,
      "performance": performance->{ _id, title, slug, poster, description, year, cast, videoUrl, photos, photoCredit, nextSlug, subtitle, premiereDate, author }
    }
  }` : null
  return useSanityData(query, { slug })
}

// Convenience hook — fetch a single performance by slug
export function usePerformance(slug) {
  const query = slug ? `*[_type == "performance" && slug.current == $slug][0]{
    _id, title, slug, subtitle, description, year, premiereDate,
    cast, poster, photos, photoCredit, videoUrl, nextSlug, navSection, navOrder
  }` : null
  return useSanityData(query, { slug })
}

// Convenience hook — fetch a single opinion by slug
export function useOpinion(slug) {
  const query = slug ? `*[_type == "opinion" && slug.current == $slug][0]{
    _id, title, slug, author, source, publishDate, aboutShow, coverImage, body
  }` : null
  return useSanityData(query, { slug })
}

// Convenience hook — fetch a platform course by slug
export function usePlatformCourse(slug) {
  const query = slug ? `*[_type == "platformCourse" && slug.current == $slug][0]{
    _id, title, slug, year, poster, description, videoUrl, materials
  }` : null
  return useSanityData(query, { slug })
}

// Convenience hook — fetch a festival event by slug
export function useFestivalEvent(slug) {
  const query = slug ? `*[_type == "festivalEvent" && slug.current == $slug][0]{
    _id, title, slug, eventDate, poster, description, photos
  }` : null
  return useSanityData(query, { slug })
}

// Convenience hook — fetch all nav items (pages + performances with navSection != 'hidden')
export function useNavItems() {
  const query = `{
    "pages": *[_type == "page" && navSection != "hidden"] | order(navOrder asc) {
      _id, "title": title, "slug": slug.current, navSection, navOrder
    },
    "performances": *[_type == "performance" && navSection != "hidden"] | order(navOrder asc) {
      _id, "title": title, "slug": slug.current, navSection, navOrder
    },
    "settings": *[_type == "siteSettings"][0] {
      navSections
    }
  }`
  return useSanityData(query)
}
